import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './components/pages/Account.css';

// Componentss
import ScrollToTop from './components/shared/ScrollToTop';
import PrivateRoute from './layouts/privateRoute';
import PublicRoute from './layouts/publicRoute';
import AuthCallback from './components/auth/AuthCallback';
import useUserProfile from './hooks/useUserProfile';
import PageTracker from './components/shared/PageTracker';
// Lazy load components
const Home = lazy(() => import('./components/pages/Home'));
const NotFound = lazy(() => import('./components/pages/NotFound'));
const DiscoverOpportunities = lazy(() => import('./components/pages/DiscoverOpportunities'));
const StepByStepGuidance = lazy(() => import('./components/pages/StepByStepGuidance'));
const ValidateYourModel = lazy(() => import('./components/pages/ValidateYourModel'));
const ExecuteAndGrow = lazy(() => import('./components/pages/ExecuteAndGrow'));
const GettingStartedGuide = lazy(() => import('./components/pages/GettingStartedGuide'));
const FeaturesAndTools = lazy(() => import('./components/pages/FeaturesAndTools'));
const Dashboard = lazy(() => import('./components/pages/Dashboard'));
const PurchasePage = lazy(() => import('./components/pages/PurchasePage'));
const EduMarketValidation = lazy(() => import('./components/pages/EduMarketValidation'));
const AccountOverview = lazy(() => import('./components/pages/AccountOverview'));
const SubscriptionBilling = lazy(() => import('./components/pages/SubscriptionBilling'));
const PrivacySecurity = lazy(() => import('./components/pages/PrivacySecurity'));
const Notifications = lazy(() => import('./components/pages/Notifications'));
const Account = lazy(() => import('./components/pages/Account'));
const PricingPlans = lazy(() => import('./components/pages/PricingPlans'));
const ThankYouPage = lazy(() => import('./components/pages/ThankYouPage'));
const ProPlanDetails = lazy(() => import('./components/pages/ProPlanDetails'));
const MarketValidationReport = lazy(() => import('./components/pages/MarketValidationReport'));
const BusinessIdeaAnalyzer = lazy(() => import('./components/pages/BusinessIdeaAnalyzer'));
const BusinessIdeas = lazy(() => import('./components/pages/BusinessIdeas'));
const ExploratoryGuidance = lazy(() => import('./components/pages/ExploratoryGuidance'));
const ExtraIncomeGuidance = lazy(() => import('./components/pages/ExtraIncomeGuidance'));
const EduBusinessPlan = lazy(() => import('./components/pages/EduBusinessPlan'));
const BusinessPlanOptions = lazy(() => import('./components/pages/BusinessPlanOptions'));
const BusinessPlan = lazy(() => import('./components/pages/BusinessPlan'));
const AiBusinessPlan = lazy(() => import('./components/pages/AiBusinessPlan'));
const BusinessPlanPreview = lazy(() => import('./components/pages/BusinessPlanPreview'));
const EmailVerification = lazy(() => import('./components/pages/EmailVerification'));
const EmailConfirmation = lazy(() => import('./components/pages/EmailConfirmation'));
const ProfileEdit = lazy(() => import('./components/pages/ProfileEdit'));
const SimplifiedAnalysisReport = lazy(() => import('./components/pages/SimplifiedAnalysisReport'));
const LearningCenter = lazy(() => import('./components/pages/LearningCenter'));
const EditAccount = lazy(() => import('./components/pages/EditAccount'));
const ExpertNetwork = lazy(() => import('./components/pages/ExpertNetwork'));
const BusinessScenarioSimulator = lazy(() => import('./components/pages/BusinessScenarioSimulator'));
const BusinessPlansMadeEasy = lazy(() => import('./components/pages/BusinessPlansMadeEasy'));
const AboutUs = lazy(() => import('./components/pages/AboutUs'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/pages/TermsOfService'));
const FeaturesPage = lazy(() => import('./components/pages/FeaturesPage'));
const ContentViewer = lazy(() => import('./components/learning/components/ContentViewer'));
const IndustryExpertAdvisorHub = lazy(() => import('./components/pages/IndustryExpertAdvisorHub'));
const ForgotPasswordConfirmation = lazy(() => import('./components/pages/ForgotPasswordConfirmation.js'));
const ResetPassword = lazy(() => import('./components/pages/ResetPassword'));
const BusinessPlanReport = lazy(() => import('./components/pages/BusinessReport'));
const CancelSubscription = lazy(() => import('./components/pages/CancelSubscription'));
const InvestmentGuidance = lazy(() => import('./components/pages/InvestmentGuidance'));
const CryptoMarketOverview = lazy(() => import('./components/pages/CryptoMarketOverview'));
const BusinessPlanGenerator = lazy(() => import('./components/pages/BusinessPlanGenerator'));
const Blog = lazy(() => import('./components/pages/Blog'));
const BlogDetail = lazy(() => import('./components/pages/BlogDetail'));
const ActivitiesPage = lazy(() => import('./components/pages/ActivitiesPage'));
const AffiliateDashboard = lazy(() => import('./components/pages/AffiliateDashboard'));
const AffiliateTerms = lazy(() => import('./components/pages/AffiliateTerms'));
const AffiliateMarketingFAQ = lazy(() => import('./components/pages/AffiliateMarketingFAQ'));
const AffiliateMarketingBestPractices = lazy(() => import('./components/pages/AffiliateMarketingBestPractices'));
const AffiliateAgreement = lazy(() => import('./components/pages/AffiliateAgreement'));
const BlogAdminLayout = lazy(() => import('./components/pages/BlogAdminLayout'));
const BlogAnalyticsDashboard = lazy(() => import('./components/pages/BlogAnalyticsDashboard'));
const BlogPosts = lazy(() => import('./components/pages/BlogPosts'));
const BlogUsers = lazy(() => import('./components/pages/BlogUsers'));
const BlogSettings = lazy(() => import('./components/pages/BlogSettings'));
const BlogAdminRoutes = lazy(() => import('./components/pages/BlogAdminRoutes'));
const BlogCategories = lazy(() => import('./components/pages/BlogCategories'));
const BlogComments = lazy(() => import('./components/pages/BlogComments'));
const BlogPostEditor = lazy(() => import('./components/pages/BlogPostEditor'));
const BlogManagementDashboard = lazy(() => import('./components/pages/BlogManagementDashboard'));
const AffiliateSignup = lazy(() => import('./components/pages/AffiliateSignup.js'));
const AffiliateSignupConfirmation = lazy(() => import('./components/pages/AffiliateSignupConfirmation.js'));
const AffiliateConsole = lazy(() => import('./components/pages/AffiliateConsole.js'));
const AffiliateOnboardingRefresh = lazy(() => import('./components/pages/AffiliateOnboardingRefresh.js'));
const AffiliateStripeRedirect = lazy(() => import('./components/pages/AffiliateStripeRedirect.js'));
const CommunityPage = lazy(() => import('./components/pages/CommunityPage'));
const MySavedBusinessPlans = lazy(() => import('./components/pages/MySavedBusinessPlans'));
const MySavedIdeas = lazy(() => import('./components/pages/MySavedIdeas.js'));
const BusinessIdeaDevelopmentEngine = lazy(() => import('./components/pages/BusinessIdeaDevelopmentEngine.js'));
const PathFinderCustomCheckout = lazy(() => import('./components/pages/PathFinderCustomCheckout'));
const FreeUserDashboard = lazy(() => import('./components/pages/FreeUserDashboard'));
const ValidationEngine = lazy(() => import('./components/pages/ValidationEngine'));
const ValidationValueDemo = lazy(() => import('./components/pages/ValidationValueDemo.js'));
const SupportManagementPortal = lazy(() => import('./components/pages/SupportManagementPortal'));
const SupportPortal = lazy(() => import('./components/pages/SupportPortal'));
const CampaignManagementSystem = lazy(() => import('./components/pages/CampaignManagementSystem'));
const PathFinderVideos = lazy(() => import('./components/pages/PathFinderVideos'));
const PathFinderAnalytics = lazy(() => import('./components/pages/PathFinderAnalytics'));
const PathFinderAnalyticsTracker = lazy(() => import('./components/pages/PathFinderAnalyticsTracker'));
const ManagementConsole = lazy(() => import('./components/pages/ManagementConsole.js'));
const PathFinderFeatureAnalytics = lazy(() => import('./components/pages/PathFinderFeatureAnalytics'));
const PathFinderGlobalTranslator = lazy(() => import('./components/pages/PathFinderGlobalTranslator.js'));
const FloatingDashboard = lazy(() => import('./components/pages/FloatingDashboard.js'));
const OnePager = lazy(() => import('./components/pages/OnePager'));
const PathFinderConnect = lazy(() => import('./components/pages/PathFinderConnect.js'));
const PathfinderSynthesizedSearch = lazy(() => import('./components/pages/PathfinderSynthesizedSearch'));
const PathfinderSynthesizedResults = lazy(() => import('./components/pages/PathfinderSynthesizedResults'));
const PathfinderSynthesizedWrapper = lazy(() => import('./components/pages/PathfinderSynthesizedWrapper'));
const PathFinderNewsletter = lazy(() => import('./components/pages/PathFinderNewsletter'));
const PathFinderPublicNewsletterPage = lazy(() => import('./components/pages/PathFinderPublicNewsletterPage'));
const BusinessInABox = lazy(() => import('./components/pages/BusinessInABox'));
const BusinessInABoxProjects = lazy(() => import('./components/pages/BusinessInABoxProjects'));
const PathFinderExchangeOpenSource = lazy(() => import('./components/pages/PathFinderExchangeOpenSource'));
const PathFinderExchangeContribution = lazy(() => import('./components/pages/PathFinderExchangeContribution'));
const PathFinderExchangePosting = lazy(() => import('./components/pages/PathFinderExchangePosting'));
const LinkedInProfileGenerator = lazy(() => import('./components/pages/LinkedInProfileGenerator'));
const PathFinderIdeaExchange = lazy(() => import('./components/pages/PathFinderIdeaExchange'));
const PathFinderScenarios = lazy(() => import('./components/pages/PathFinderScenarios.js'));
const FeatureManagementPreference = lazy(() => import('./components/pages/FeatureManagementPreference'));
const SixtySeconds = lazy(() => import('./components/pages/SixtySeconds'));
// const PersonalDashboard = lazy(() => import('./components/pages/PersonalDashboard'))
// const Dashboard2 = lazy(() => import('./components/pages/Dashboard2'))
const FiveMinuteWebsite = lazy(() => import('./components/pages/5MinuteWebsite'));

// FeatureManagementConsole
const FeatureManagementConsole = lazy(() => import('./components/pages/FeatureManagementConsole.js'));
const PathFinderProjectManagement = lazy(() => import('./components/pages/PathFinderProjectManagement.js'));
const PathFinderWaterfallManagement = lazy(() => import('./components/pages/PathFinderWaterfallManagement.js'));

const PathFinderAiGiveaway = lazy(() => import('./components/pages/PathFinderAiGiveaway'));
const PathFinderGiveawayRules = lazy(() => import('./components/pages/PathFinderGiveawayRules'));
const PathFinderScenariosMyBusinesses = lazy(() => import('./components/pages/PathFinderScenariosMyBusinesses.js'));
// import PathFinderSalesAgentManager from '';
const PathFinderSalesAgentManager = lazy(() => import('./components/pages/PathFinderSalesAgentManager'));
const PathFinderInfo = lazy(() => import('./components/pages/PathFinderInfo'));
const PathFinderInfoAdmin = lazy(() => import('./components/pages/PathFinderInfoAdmin'));
// const PathFinderWebBuilder = lazy(() => import('./components/pages/PathFinderWebBuilder'));
const WebBuilder = lazy(() => import('./components/pages/WebBuilder'));
const LandingPageBuilder = lazy(() => import('./components/pages/LandingPageBuilder'));
const LandingPageAccessGuard = lazy(() => import('./components/common/LandingPageAccessGuard'));
const WebBuilderPreview = lazy(() => import('./components/pages/WebBuilderPreview'));
const WebBuilderCheckoutSuccess = lazy(() => import('./components/pages/WebBuilderCheckoutSuccess'));
const WebBuilderCheckoutCancel = lazy(() => import('./components/pages/WebBuilderCheckoutCancel'));
const WebBuilderSalesOnboarding = lazy(() => import('./components/pages/WebBuilderSalesOnboarding'));
const WebBuilderOnboardingReview = lazy(() => import('./components/pages/WebBuilderOnboardingReview'));
const WebBuilderWeeklyPayrollReport = lazy(() => import('./components/pages/WebBuilderWeeklyPayrollReport'));
const WebBuilderSalesTeamEnrollment = lazy(() => import('./components/pages/WebBuilderSalesTeamEnrollment'));
const PathFinderTrainingPortal = lazy(() => import('./components/pages/PathFinderTrainingPortal'));
const WebBuilderLabs = lazy(() => import('./components/pages/WebBuilderLabs'));
const WebBuilderGuard = lazy(() => import('./components/pages/WebBuilderGuard'));
const WebBuilderPostDemoConsultation = lazy(() => import('./components/pages/WebBuilderPostDemoConsultation'));
const WebBuilderPostDemoAdmin = lazy(() => import('./components/pages/WebBuilderPostDemoAdmin'));
// import WebBuilderPostDemoConsultation from './components/pages/WebBuilderPostDemoConsultation';
// import WebBuilderPostDemoAdmin from './components/pages/WebBuilderPostDemoAdmin';
// import WebBuilderScriptGenerator from "./components/pages/WebBuilderScriptGenerator";
const WebBuilderScriptGenerator = lazy(() => import('./components/pages/WebBuilderScriptGenerator'));
const PostDemoFollowUp = lazy(() => import('./components/pages/PostDemoFollowUp'));
const WebBuilder99StartUp = lazy(() => import('./components/pages/WebBuilder$99StartUp.js'));
const WebBuilderTenSites = lazy(() => import('./components/pages/WebBuilderTenSites.js'));

const UnsubscribeWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const hasUnsubscribeParams = urlParams.get('email') && urlParams.get('token');
  const isUnsubscribeRoute = location.pathname === '/unsubscribeCampaign';

  React.useEffect(() => {
    try {
      const redirectRoute = localStorage.getItem('pf_pending_redirect');
      const isLoggedIn = !!localStorage.getItem('userToken') || !!localStorage.getItem('userEmail');
      if (isLoggedIn && redirectRoute) {
        localStorage.removeItem('pf_pending_redirect');
        window.location.assign(redirectRoute);
      }
    } catch (err) {
      console.error('post-login redirect check failed', err);
    }
  }, []);
  // Auto-close window after 2 minutes only for unsubscribe route
  React.useEffect(() => {
    if (!isUnsubscribeRoute || !hasUnsubscribeParams) return;

    const timer = setTimeout(() => {
      if (window.opener) {
        window.close();
      } else {
        window.location.href = '/';
      }
    }, 60000); // 2 minutes

    return () => clearTimeout(timer);
  }, [isUnsubscribeRoute, hasUnsubscribeParams]);

  if (!hasUnsubscribeParams) {
    return <Navigate to="/" replace />;
  }

  return <CampaignManagementSystem />;
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-800"></div>
  </div>
);
const ConditionalMantraBanner = () => {
  const location = useLocation().pathname;
  const isOnePagerRoute = location === '/OnePager';
  const isWebBuilderPreviewRoute = location.startsWith('/preview/');
  const FiveMinuteWebsiteRoute = location === '/5MinuteWebsite';
  const isPublishedSiteRoute = location.startsWith('/site/');
  const WebBuilderTenSites = location === '/build-10-websites';
  const WebBuilder99StartUp = location === '/WebBuilder99StartUp';
  if (
    location === '/' ||
    isOnePagerRoute ||
    isPublishedSiteRoute ||
    isWebBuilderPreviewRoute ||
    FiveMinuteWebsiteRoute ||
    WebBuilderTenSites ||
    WebBuilder99StartUp ||
    location.startsWith('/LandingPageBuilder')
  ) {
    return null;
  }

  return (
    <div className="sticky top-0 bg-[#8F3BF4] text-white py-4 z-[100]" style={{ color: 'white' }}>
      <div className="container mx-auto px-4 text-center">
        <p className="text-lg" style={{ color: 'white' }}>
          <span className="font-bold" style={{ color: 'white' }}>
            Start Your Business.
          </span>{' '}
          Build Your Website in Under a Minute.
        </p>
      </div>
    </div>
  );
};
const RedirectWithReferral = () => {
  const { referralCode } = useParams();
  console.log(referralCode, 'referal');
  return <Navigate to={{ pathname: '/', search: `?ref=${referralCode}` }} replace />;
};

const WEB_BUILDER_FALLBACK_HOSTED_SITE_DOMAINS = ['sites.pathfinder-ai.net', 'stage-sites.pathfinder-ai.net'];

const getWebBuilderHostedSiteDomain = () =>
  String(process.env.REACT_APP_WEB_BUILDER_HOSTED_SITE_DOMAIN || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();

const isWebBuilderHostedSiteDomain = (hostname = '') => {
  const normalizedHostname = String(hostname || '')
    .trim()
    .replace(/^www\./i, '')
    .toLowerCase();

  const configuredDomain = getWebBuilderHostedSiteDomain();

  const allowedDomains = [configuredDomain, ...WEB_BUILDER_FALLBACK_HOSTED_SITE_DOMAINS].filter(Boolean);

  return allowedDomains.includes(normalizedHostname);
};

const isWebBuilderRoute = (pathname, hostname) =>
  isWebBuilderHostedSiteDomain(hostname || window.location.hostname) ||
  pathname.startsWith('/site/') ||
  pathname.startsWith('/WebBuilder') ||
  pathname.startsWith('/LandingPageBuilder') ||
  pathname.startsWith('/PathFinderWebBuilder') ||
  pathname.startsWith('/PathFinderCustomCheckout') ||
  pathname.startsWith('/preview/') ||
  pathname.startsWith('/web-builder-checkout-success') ||
  pathname.startsWith('/web-builder-checkout-cancel') ||
  pathname.startsWith('/PostDemoFollowUp');

const isTrainingPortalRoute = (pathname = '') => pathname.startsWith('/PathFinderTrainingPortal');

const HostedWebBuilderSiteRoute = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (!isWebBuilderHostedSiteDomain(hostname)) {
    return (
      <PageTracker pageName="Page Not Found">
        <NotFound />
      </PageTracker>
    );
  }

  return <WebBuilderPreview publishedMode={true} />;
};

function App() {
  useUserProfile();
  const [showSignup, setShowSignup] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  console.warn = () => {};

  const isHostedWebBuilderDomain = typeof window !== 'undefined' && isWebBuilderHostedSiteDomain(window.location.hostname);

  if (isHostedWebBuilderDomain) {
    return (
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="*" element={<WebBuilderPreview publishedMode={true} />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <PathFinderAnalyticsTracker />
      {/* {!isWebBuilderRoute(window.location.pathname) && !isTrainingPortalRoute(window.location.pathname) && (
        <React.Suspense fallback={null}>
          <PathFinderSalesAgentManager />
        </React.Suspense>
      )} */}

      <ConditionalMantraBanner />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicRoute />}>
            <Route
              path="/"
              element={
                <PageTracker pageName="Home">
                  <Home />
                </PageTracker>
              }
            />
            <Route path="/newsletter/:slug" element={<PathFinderPublicNewsletterPage />} />
            <Route path="/auth/google/success" element={<AuthCallback />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route path="/forgot-password" element={<ForgotPasswordConfirmation />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/about-us"
              element={
                <PageTracker pageName="About Us">
                  <AboutUs />
                </PageTracker>
              }
            />
            <Route
              path="/WebBuilder99StartUp"
              element={
                <PageTracker pageName="Web Builder $99 StartUp">
                  <WebBuilder99StartUp />
                </PageTracker>
              }
            />
            <Route
              path="/build-10-websites"
              element={
                <PageTracker pageName="build-10-websites">
                  <WebBuilderTenSites />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderCustomCheckout"
              element={
                <PageTracker pageName="PathFinder Custom Checkout">
                  <PathFinderCustomCheckout />
                </PageTracker>
              }
            />

            <Route
              path="/5MinuteWebsite"
              element={
                <PageTracker pageName="5 Minute Website">
                  <FiveMinuteWebsite />
                </PageTracker>
              }
            />
            <Route
              path="/privacy"
              element={
                <PageTracker pageName="Privacy Policy">
                  <PrivacyPolicy />
                </PageTracker>
              }
            />
            <Route
              path="/terms"
              element={
                <PageTracker pageName="Terms of Service">
                  <TermsOfService />
                </PageTracker>
              }
            />

            <Route
              path="/Blog"
              element={
                <PageTracker pageName="Blog">
                  <Blog />
                </PageTracker>
              }
            />
            <Route
              path="/WebBuilderPostDemoConsultation"
              element={
                <PageTracker pageName="Web Builder Post Demo Consultation">
                  <WebBuilderPostDemoConsultation />
                </PageTracker>
              }
            />
            {/* <Route
              path="/PersonalDashboard"
              element={
                <PageTracker pageName="Personal Dashboard">
                  <PersonalDashboard />
                </PageTracker>
              }
            /> */}
            <Route
              path="/OnePager"
              element={
                <PageTracker pageName="OnePager">
                  <OnePager />
                </PageTracker>
              }
            />

            <Route
              path="/blog/:slug"
              element={
                <PageTracker pageName="Blog Detail">
                  <BlogDetail />
                </PageTracker>
              }
            />
            <Route path="/unsubscribeCampaign" element={<UnsubscribeWrapper />} />
            <Route
              path="/features-page"
              element={
                <PageTracker pageName="Features Page">
                  <FeaturesPage />
                </PageTracker>
              }
            />
          </Route>

          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route
              path="/thank-you"
              element={
                <PageTracker pageName="Thank You">
                  <ThankYouPage />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderProjectManagement"
              element={
                <PageTracker pageName="PathFinder Project Management">
                  <PathFinderProjectManagement />
                </PageTracker>
              }
            />
            <Route
              path="/SixtySeconds"
              element={
                <PageTracker pageName="Sixty Seconds">
                  <SixtySeconds />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderWaterfallManagement"
              element={
                <PageTracker pageName="PathFinder Waterfall Management">
                  <PathFinderWaterfallManagement />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderScenarios"
              element={
                <PageTracker pageName="PathFinderScenarios">
                  <PathFinderScenarios />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderConnect"
              element={
                <PageTracker pageName="PathFinderConnect">
                  <PathFinderConnect />
                </PageTracker>
              }
            />

            <Route
              path="/LinkedInProfileGenerator"
              element={
                <PageTracker pageName="LinkedIn Profile Generator">
                  <LinkedInProfileGenerator />
                </PageTracker>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PageTracker pageName="User Dashboard">
                  <Dashboard />
                </PageTracker>
              }
            />
            <Route
              path="/free-dashboard"
              element={
                <PageTracker pageName="Free User Dashboard">
                  <FreeUserDashboard />
                </PageTracker>
              }
            />
            <Route
              path="/CancelSubscription"
              element={
                <PageTracker pageName="Cancel Subscription">
                  <CancelSubscription />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderIdeaExchange"
              element={
                <PageTracker pageName="PathFinder Idea Exchange">
                  <PathFinderIdeaExchange />
                </PageTracker>
              }
            />

            <Route path="/WebBuilderPostDemoAdmin" element={<WebBuilderPostDemoAdmin />} />
            <Route
              path="/PathFinderExchangeOpenSource"
              element={
                <PageTracker pageName="PathFinder Exchange OpenSource">
                  <PathFinderExchangeOpenSource />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderExchangeContribution"
              element={
                <PageTracker pageName="PathFinder Exchange Contribution">
                  <PathFinderExchangeContribution />
                </PageTracker>
              }
            />
            <Route
              path="/PostDemoFollowUp"
              element={
                <PageTracker pageName="Post Demo FollowUp">
                  <PostDemoFollowUp />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderExchangePosting"
              element={
                <PageTracker pageName="PathFinder Exchange Posting">
                  <PathFinderExchangePosting />
                </PageTracker>
              }
            />
            <Route
              path="/discover-opportunities"
              element={
                <PageTracker pageName="Discover Opportunities">
                  <DiscoverOpportunities />
                </PageTracker>
              }
            />
            <Route
              path="/account"
              element={
                <PageTracker pageName="Account">
                  <Account />
                </PageTracker>
              }
            />
            <Route
              path="/BusinessInABox"
              element={
                <PageTracker pageName="Business In A Box">
                  <BusinessInABox />
                </PageTracker>
              }
            />
            <Route
              path="/BusinessInABoxProjects"
              element={
                <PageTracker pageName="Business In A Box Projects">
                  <BusinessInABoxProjects />
                </PageTracker>
              }
            />
            <Route path="/PathFinderNewsletter" element={<PathFinderNewsletter />} />
            <Route path="/PathFinderPublicNewsletterPage" element={<PathFinderPublicNewsletterPage />} />
            <Route
              path="/edit-account"
              element={
                <PageTracker pageName="Edit Account">
                  <EditAccount />
                </PageTracker>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <PageTracker pageName="Edit Profile">
                  <ProfileEdit />
                </PageTracker>
              }
            />
            <Route
              path="/market-validation-education"
              element={
                <PageTracker pageName="Market Validation Education">
                  <EduMarketValidation />
                </PageTracker>
              }
            />
            <Route
              path="/market-validation-education/:ideaId"
              element={
                <PageTracker pageName="Market Validation Education">
                  <EduMarketValidation />
                </PageTracker>
              }
            />
            <Route
              path="/analysis-report"
              element={
                <PageTracker pageName="Analysis Report">
                  <SimplifiedAnalysisReport />
                </PageTracker>
              }
            />
            <Route
              path="/analysis-report/:businessId"
              element={
                <PageTracker pageName="Analysis Report">
                  <SimplifiedAnalysisReport />
                </PageTracker>
              }
            />

            <Route
              path="/PathFinderGlobalTranslator"
              element={
                <PageTracker pageName="PathFinder Global Translator">
                  <PathFinderGlobalTranslator />
                </PageTracker>
              }
            />
            {/* <Route
              path="/PathFinderWebBuilder"
              element={
                <PageTracker pageName="PathFinder Web Builder">
                  <PathFinderWebBuilder />
                </PageTracker>
              }
            /> */}

            <Route
              path="/WebBuilder"
              element={
                <PageTracker pageName="Web Builder">
                  <WebBuilderGuard />
                </PageTracker>
              }
            />
            <Route
              path="/WebBuilderLabs"
              element={
                <PageTracker pageName="Web Builder Labs">
                  <WebBuilderLabs />
                </PageTracker>
              }
            />
            <Route
              path="/WebBuilderScriptGenerator"
              element={
                <PageTracker pageName="Web Builder Script Generator">
                  <WebBuilderScriptGenerator />
                </PageTracker>
              }
            />
            <Route path="/LandingPageBuilder" element={<LandingPageAccessGuard><LandingPageBuilder /></LandingPageAccessGuard>} />
            <Route path="/preview/:projectId" element={<WebBuilderPreview />} />
            <Route path="/site/:slug" element={<WebBuilderPreview publishedMode={true} />} />
            <Route path="/:slug" element={<HostedWebBuilderSiteRoute />} />
            <Route
              path="/web-builder-checkout-success"
              element={
                <PageTracker pageName="Web Builder Checkout Success">
                  <WebBuilderCheckoutSuccess />
                </PageTracker>
              }
            />

            {/* --- WebBuilder Sales Onboarding Routes --- */}
            <Route
              path="/WebBuilderSalesOnboarding"
              element={
                <PageTracker pageName="WebBuilder Sales Onboarding">
                  <WebBuilderSalesOnboarding />
                </PageTracker>
              }
            />

            <Route
              path="/PathFinderTrainingPortal"
              element={
                <PageTracker pageName="PathFinder Training Portal">
                  <PathFinderTrainingPortal />
                </PageTracker>
              }
            />

            <Route
              path="/web-builder-checkout-cancel"
              element={
                <PageTracker pageName="Web Builder Checkout Cancel">
                  <WebBuilderCheckoutCancel />
                </PageTracker>
              }
            />
            <Route
              path="/my-businesses"
              element={
                <PageTracker pageName="PathFinder Scenarios My Businesses">
                  <PathFinderScenariosMyBusinesses />
                </PageTracker>
              }
            />
            <Route
              path="/business-ideas"
              element={
                <PageTracker pageName="Business Ideas">
                  <BusinessIdeas />
                </PageTracker>
              }
            />
            <Route
              path="/edu-business-plan"
              element={
                <PageTracker pageName="Business Plan Education">
                  <EduBusinessPlan />
                </PageTracker>
              }
            />
            <Route
              path="/business-plan-options"
              element={
                <PageTracker pageName="Business Plan Options">
                  <BusinessPlanOptions />
                </PageTracker>
              }
            />
            <Route
              path="/business-plan"
              element={
                <PageTracker pageName="Business Plan">
                  <BusinessPlan />
                </PageTracker>
              }
            />
            <Route
              path="/ai-business-plan"
              element={
                <PageTracker pageName="AI Business Plan">
                  <AiBusinessPlan />
                </PageTracker>
              }
            />

            <Route
              path="/getting-started"
              element={
                <PageTracker pageName="Getting Started Guide">
                  <GettingStartedGuide />
                </PageTracker>
              }
            />
            <Route
              path="/step-by-step-guidance"
              element={
                <PageTracker pageName="Step-by-Step Guidance">
                  <StepByStepGuidance />
                </PageTracker>
              }
            />
            <Route
              path="/validate-your-model"
              element={
                <PageTracker pageName="Validate Your Model">
                  <ValidateYourModel />
                </PageTracker>
              }
            />
            <Route
              path="/execute-and-grow"
              element={
                <PageTracker pageName="Execute and Grow">
                  <ExecuteAndGrow />
                </PageTracker>
              }
            />
            <Route
              path="/features-and-tools"
              element={
                <PageTracker pageName="Features and Tools">
                  <FeaturesAndTools />
                </PageTracker>
              }
            />
            <Route
              path="/purchase-page"
              element={
                <PageTracker pageName="Purchase Page">
                  <PurchasePage />
                </PageTracker>
              }
            />
            <Route
              path="/account-overview"
              element={
                <PageTracker pageName="Account Overview">
                  <AccountOverview />
                </PageTracker>
              }
            />
            <Route
              path="/subscription-billing"
              element={
                <PageTracker pageName="Subscription Billing">
                  <SubscriptionBilling />
                </PageTracker>
              }
            />
            <Route
              path="/privacy-security"
              element={
                <PageTracker pageName="Privacy Security">
                  <PrivacySecurity />
                </PageTracker>
              }
            />
            <Route
              path="/notifications"
              element={
                <PageTracker pageName="Notifications">
                  <Notifications />
                </PageTracker>
              }
            />
            <Route
              path="/pro-plan-details"
              element={
                <PageTracker pageName="Pro Plan Details">
                  <ProPlanDetails />
                </PageTracker>
              }
            />

            <Route
              path="/PathfinderSynthesizedResults"
              element={
                <PageTracker pageName="Pathfinder Synthesized Results">
                  <PathfinderSynthesizedResults />
                </PageTracker>
              }
            />

            <Route
              path="/PathfinderSynthesizedSearch"
              element={
                <PageTracker pageName="Pathfinder Synthesized Search">
                  <PathfinderSynthesizedWrapper />
                </PageTracker>
              }
            />
            <Route
              path="/market-validation-report"
              element={
                <PageTracker pageName="Market Validation Report">
                  <MarketValidationReport />
                </PageTracker>
              }
            />
            <Route
              path="/business-idea-analyzer"
              element={
                <PageTracker pageName="Business Idea Analyzer">
                  <BusinessIdeaAnalyzer />
                </PageTracker>
              }
            />
            <Route
              path="/explore"
              element={
                <PageTracker pageName="Exploratory Guidance">
                  <ExploratoryGuidance />
                </PageTracker>
              }
            />
            <Route
              path="/extra-income"
              element={
                <PageTracker pageName="Extra Income Guidance">
                  <ExtraIncomeGuidance />
                </PageTracker>
              }
            />
            <Route
              path="/business-plan-preview"
              element={
                <PageTracker pageName="Business Plan Preview">
                  <BusinessPlanPreview />
                </PageTracker>
              }
            />
            <Route
              path="/expert-network"
              element={
                <PageTracker pageName="Expert Network">
                  <ExpertNetwork />
                </PageTracker>
              }
            />
            <Route
              path="/business-scenario-simulator"
              element={
                <PageTracker pageName="Business Scenario Simulator">
                  <BusinessScenarioSimulator />
                </PageTracker>
              }
            />
            <Route
              path="/business-plans-made-easy"
              element={
                <PageTracker pageName="Business Plans Made Easy">
                  <BusinessPlansMadeEasy />
                </PageTracker>
              }
            />
            <Route
              path="/business-report"
              element={
                <PageTracker pageName="Business Report">
                  <BusinessPlanReport />
                </PageTracker>
              }
            />
            <Route
              path="/industry-expert-advisor-hub"
              element={
                <PageTracker pageName="Industry Expert Advisor Hub">
                  <IndustryExpertAdvisorHub />
                </PageTracker>
              }
            />
            <Route
              path="/investment-guidance"
              element={
                <PageTracker pageName="Investment Guidance">
                  <InvestmentGuidance />
                </PageTracker>
              }
            />
            <Route
              path="/crypto-market-overview"
              element={
                <PageTracker pageName="Crypto Market Overview">
                  <CryptoMarketOverview />
                </PageTracker>
              }
            />
            <Route
              path="/business-plan-generator"
              element={
                <PageTracker pageName="Business Plan Generator">
                  <BusinessPlanGenerator />
                </PageTracker>
              }
            />

            <Route
              path="/BlogDetail"
              element={
                <PageTracker pageName="Blog Detail">
                  <BlogDetail />
                </PageTracker>
              }
            />
            <Route
              path="/ActivitiesPage"
              element={
                <PageTracker pageName="Activities Page">
                  <ActivitiesPage />
                </PageTracker>
              }
            />

            <Route
              path="/AffiliateDashboard"
              element={
                <PageTracker pageName="Affiliate Dashboard">
                  <AffiliateDashboard />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateSignup"
              element={
                <PageTracker pageName="Affiliate Signup">
                  <AffiliateSignup />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateSignupConfirmation"
              element={
                <PageTracker pageName="Affiliate Signup Confirmation">
                  <AffiliateSignupConfirmation />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateTerms"
              element={
                <PageTracker pageName="Affiliate Terms">
                  <AffiliateTerms />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateMarketingFAQ"
              element={
                <PageTracker pageName="Affiliate Marketing FAQ">
                  <AffiliateMarketingFAQ />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateMarketingBestPractices"
              element={
                <PageTracker pageName="Affiliate Marketing Best Practices">
                  <AffiliateMarketingBestPractices />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateAgreement"
              element={
                <PageTracker pageName="Affiliate Agreement">
                  <AffiliateAgreement />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateConsole"
              element={
                <PageTracker pageName="Affiliate Console">
                  <AffiliateConsole />
                </PageTracker>
              }
            />
            <Route
              path="/BlogAdminLayout"
              element={
                <PageTracker pageName="Blog Admin Layout">
                  <BlogAdminLayout />
                </PageTracker>
              }
            />
            <Route
              path="/BlogAnalyticsDashboard"
              element={
                <PageTracker pageName="Blog Analytics Dashboard">
                  <BlogAnalyticsDashboard />
                </PageTracker>
              }
            />
            <Route
              path="/BlogPosts"
              element={
                <PageTracker pageName="Blog Posts">
                  <BlogPosts />
                </PageTracker>
              }
            />
            <Route
              path="/BlogUsers"
              element={
                <PageTracker pageName="Blog Users">
                  <BlogUsers />
                </PageTracker>
              }
            />
            <Route
              path="/BlogSettings"
              element={
                <PageTracker pageName="Blog Settings">
                  <BlogSettings />
                </PageTracker>
              }
            />
            <Route
              path="/BlogAdminRoutes"
              element={
                <PageTracker pageName="Blog Admin Routes">
                  <BlogAdminRoutes />
                </PageTracker>
              }
            />
            <Route
              path="/BlogCategories"
              element={
                <PageTracker pageName="Blog Categories">
                  <BlogCategories />
                </PageTracker>
              }
            />
            <Route
              path="/BlogComments"
              element={
                <PageTracker pageName="Blog Comments">
                  <BlogComments />
                </PageTracker>
              }
            />
            <Route
              path="/BlogPostEditor"
              element={
                <PageTracker pageName="Blog Post Editor">
                  <BlogPostEditor />
                </PageTracker>
              }
            />
            <Route
              path="/BlogManagementDashboard"
              element={
                <PageTracker pageName="Blog Management Dashboard">
                  <BlogManagementDashboard />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateOnboardingRefresh"
              element={
                <PageTracker pageName="Affiliate Onboarding Refresh">
                  <AffiliateOnboardingRefresh />
                </PageTracker>
              }
            />
            <Route
              path="/AffiliateStripeRedirect"
              element={
                <PageTracker pageName="Affiliate Stripe Redirect">
                  <AffiliateStripeRedirect />
                </PageTracker>
              }
            />
            <Route
              path="/MySavedIdeas"
              element={
                <PageTracker pageName="My Saved Ideas">
                  <MySavedIdeas />
                </PageTracker>
              }
            />
            <Route
              path="/BusinessIdeaDevelopmentEngine"
              element={
                <PageTracker pageName="Business Idea Development Engine">
                  <BusinessIdeaDevelopmentEngine />
                </PageTracker>
              }
            />
            <Route
              path="/MySavedBusinessPlans"
              element={
                <PageTracker pageName="My Saved Business Plans">
                  <MySavedBusinessPlans />
                </PageTracker>
              }
            />
            <Route
              path="/CommunityPage"
              element={
                <PageTracker pageName="Community Page">
                  <CommunityPage />
                </PageTracker>
              }
            />

            <Route
              path="/ValidationEngine"
              element={
                <PageTracker pageName="Validation Engine">
                  <ValidationEngine />
                </PageTracker>
              }
            />
            <Route
              path="/ValidationValueDemo"
              element={
                <PageTracker pageName="Validation Value Demo">
                  <ValidationValueDemo />
                </PageTracker>
              }
            />
            <Route
              path="/SupportManagementPortal"
              element={
                <PageTracker pageName="Support Management Portal">
                  <SupportManagementPortal />
                </PageTracker>
              }
            />
            <Route
              path="/SupportPortal"
              element={
                <PageTracker pageName="Support Portal">
                  <SupportPortal />
                </PageTracker>
              }
            />
            <Route
              path="/CampaignManagementSystem"
              element={
                <PageTracker pageName="Campaign Management System">
                  <CampaignManagementSystem />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderVideos"
              element={
                <PageTracker pageName="PathFinder Videos">
                  <PathFinderVideos />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderAnalytics"
              element={
                <PageTracker pageName="PathFinder Analytics">
                  <PathFinderAnalytics />
                </PageTracker>
              }
            />

            <Route path="/WebBuilderOnboardingReview" element={<WebBuilderOnboardingReview />} />
            <Route path="/WebBuilderPayrollReport" element={<WebBuilderWeeklyPayrollReport />} />
            <Route path="/WebBuilderSalesTeamEnrollment" element={<WebBuilderSalesTeamEnrollment />} />
            <Route
              path="/PathFinderInfo"
              element={
                <PageTracker pageName="PathFinder Info">
                  <PathFinderInfo />
                </PageTracker>
              }
            />

            <Route
              path="/PathFinderInfoAdmin"
              element={
                <PageTracker pageName="PathFinder InfoAdmin">
                  <PathFinderInfoAdmin />
                </PageTracker>
              }
            />

            <Route path="/FeatureManagementPreference" element={<FeatureManagementPreference />} />
            <Route
              path="/FeatureManagementConsole"
              element={
                <PageTracker pageName="FeatureManagementConsole">
                  <FeatureManagementConsole />
                </PageTracker>
              }
            />
            <Route
              path="/ManagementConsole"
              element={
                <PageTracker pageName="Management Console">
                  <ManagementConsole />
                </PageTracker>
              }
            />

            <Route path="/PathFinderFeatureAnalytics" element={<PathFinderFeatureAnalytics />} />

            <Route
              path="/PathFinderAiGiveaway"
              element={
                <PageTracker pageName="PathFinder Ai Giveaway">
                  <PathFinderAiGiveaway
                    showSignup={showSignup}
                    setShowSignup={setShowSignup}
                    showSignin={showSignin}
                    setShowSignin={setShowSignin}
                  />
                </PageTracker>
              }
            />
            <Route
              path="/PathFinderGiveawayRules"
              element={
                <PageTracker pageName="PathFinder Giveaway Rules">
                  <PathFinderGiveawayRules />
                </PageTracker>
              }
            />

            {/* Learning Center Routes */}
            <Route
              path="/learning"
              element={
                <PageTracker pageName="Learning Center">
                  <LearningCenter />
                </PageTracker>
              }
            >
              <Route
                path="*"
                element={
                  <PageTracker pageName="Content Viewer">
                    <ContentViewer />
                  </PageTracker>
                }
              />
            </Route>
          </Route>
          {/* Remove this route - FloatingDashboard is not a route */}
          {/* Redirect for Referral Links - No tracking needed */}
          <Route path="/ref/:referralCode" element={<RedirectWithReferral />} />
          <Route path="/webbuilder/:referralCode" element={<RedirectWithReferral />} />
          {/* 404 Route */}
          <Route
            path="*"
            element={
              <PageTracker pageName="Not Found">
                <NotFound />
              </PageTracker>
            }
          />
        </Routes>

        {/* {window.location.pathname !== '/OnePager' && (
          <FloatingDashboard
            isAuthenticated={
              !!localStorage.getItem('userToken') || !!localStorage.getItem('userEmail') || !!localStorage.getItem('authToken')
            }
            userName={null} // TODO: Replace with actual user name when authenticated
            onAuthRequired={() => {
              try {
                const intent = JSON.parse(localStorage.getItem('pf_user_intent'));
                if (intent?.route) {
                  localStorage.setItem('pf_pending_redirect', intent.route);
                }
              } catch (e) {
                console.log('jwbjwbgjwbgjwb');
              }
              // Show sign-up modal
              setShowSignup(true);
            }}
            onNavigate={(route) => {
              try {
                if (route) {
                  window.location.assign(route);
                }
              } catch (e) {
                console.error(e);
              }
            }}
          />
        )} */}
      </Suspense>
    </Router>
  );
}

export default App;
